import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { updateColumnRange } from '@/redux/slices/payoutStructure';
import styles from '../payout-structures.module.css';
import TableCellRangeInput from './TableCellRangeInput';
import { CELL_HEIGHT, CELL_WIDTH } from './VirtualizedPayoutTable';
import { roundToDecimalPlaces } from '@repo/utils';

interface ColumnHeaderProps {
  columnIndex: number;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({ columnIndex }) => {
  const dispatch = useDispatch();

  const playersColumn = useSelector((state: any) => state.payoutStructure.playersColumns[columnIndex]);
  const playersColumns = useSelector((state: any) => state.payoutStructure.playersColumns);

  const isOddColumn = columnIndex % 2 === 0;

  const columnTotal = playersColumn?.percentages?.reduce((acc: number, curr: any) => acc + Number(curr ?? 0), 0) || 0;
  const columnPlayersPaid =
    playersColumn?.percentages?.reduce((acc: number, curr: any) => {
      if (curr > 0) {
        return acc + 1;
      }
      return acc;
    }, 0) || 0;

  const columnFrom = playersColumn?.playersFrom || 1;
  const columnTo = playersColumn?.playersTo || 1;

  const columnValid = useMemo(() => {
    const previousColumn = playersColumns[columnIndex - 1];
    const isLowerThanPrevious = previousColumn && columnFrom <= previousColumn.playersTo;

    const nextColumn = playersColumns[columnIndex + 1];
    const isHigherThanNext = nextColumn && columnTo >= nextColumn.playersFrom;

    return !isLowerThanPrevious && !isHigherThanNext;
  }, [columnFrom, columnTo, playersColumns, columnIndex]);

  const handleChange = (value: { from: number; to: number }) => {
    dispatch(
      updateColumnRange({
        index: columnIndex,
        playersFrom: value.from,
        playersTo: value.to,
      }),
    );
  };

  return (
    <div
      className={classNames(styles.payoutStructureTableHeaderCellContainer, {
        [styles.oddColumn]: isOddColumn,
      })}
    >
      <TableCellRangeInput
        min={1}
        value={{
          from: columnFrom,
          to: columnTo,
        }}
        onChange={handleChange}
        invalid={!columnValid}
      />
      <div
        style={{
          height: CELL_HEIGHT,
          width: CELL_WIDTH,
        }}
        className={classNames(styles.payoutStructureTableCell, 'user-select-none')}
      >
        {columnPlayersPaid}
      </div>
      <div
        className={classNames(
          styles.payoutStructureTableCell,
          'user-select-none',
          roundToDecimalPlaces(columnTotal, 2) !== 100 ? 'text-danger' : styles.percentageComplete,
        )}
        style={{
          height: CELL_HEIGHT,
          width: CELL_WIDTH,
        }}
      >
        {columnTotal.toFixed(2)}%
      </div>
    </div>
  );
};

export default ColumnHeader;
